import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { useRegenerateMedia } from "./useRegenerateMedia";
import * as Sentry from "@sentry/react";
import { isStoryboardAwaitingApproval } from "../helpers/helper";

/**
 * Fires whichever action actually clears "media out of date": if the
 * storyboard itself changed and is waiting for review, approving it kicks
 * off a fresh generation; otherwise the storyboard is already approved and
 * only the media needs regenerating. Shared by `GenerateMediaButton` and the
 * project detail page's out-of-date widget, so both surfaces agree on what
 * "Regenerate" means.
 */
export const useRegenerateOrApprove = ({
  projectId,
  storyboardSettingsId,
  storyboardStatus,
  mediaId,
  mediaStatus,
  refetchProjectDetail = () => undefined,
}: {
  projectId: string | undefined;
  storyboardSettingsId: string | undefined;
  storyboardStatus: string | undefined;
  mediaId: string | undefined;
  mediaStatus: string | undefined;
  refetchProjectDetail?: () => void;
}) => {
  const invalidate = useInvalidate();
  const { mutateAsync: approve, isPending: isApproving } = useCustomMutation();
  const { handleRegenerateMedia, isRegenerating } = useRegenerateMedia(
    refetchProjectDetail,
    mediaId,
    projectId,
    mediaStatus
  );

  // Returns whether the regenerate/approve action actually succeeded —
  // callers (e.g. the out-of-date widget's dismissal bookkeeping) rely on
  // this to tell a real success from a swallowed failure.
  const regenerateOrApprove = async (): Promise<boolean> => {
    if (!projectId) return false;

    if (isStoryboardAwaitingApproval(storyboardStatus)) {
      try {
        await approve({
          url: `media/projects/${projectId}/storyboards/${storyboardSettingsId}/approve`,
          method: "post",
          values: {},
        });
        await invalidate({
          resource: "media/projects",
          id: projectId,
          invalidates: ["detail"],
        });
        refetchProjectDetail();
        return true;
      } catch (error) {
        console.error("Failed to approve storyboard:", error);
        Sentry.captureException(error, {
          tags: { project_id: projectId, action: "approve-storyboard" },
        });
        return false;
      }
    }

    return handleRegenerateMedia();
  };

  return { regenerateOrApprove, isProcessing: isApproving || isRegenerating };
};
